import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  SegmentedControl,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi } from './voiceClient';
import { VoiceGrokBrowserClient, VoiceEvent } from './voiceGrokClient';
import './VoiceReportingWidget.css';

interface Props {
  storeId: string;
}

/**
 * In-browser voice reporting surface.
 *
 * Live duplex stream to grok-voice-fast-1.0 via the BSO voice-relay bridge
 * (services/voice-relay/src/app-client.js). Mic audio is captured at
 * 24 kHz PCM16 by an AudioWorklet, base64-framed in 20 ms slices, and
 * shipped over a WebSocket. Inbound audio is decoded back to PCM16 and
 * scheduled with the AudioContext clock so playback never stutters.
 *
 * Session lifecycle is recorded through the existing voiceApi
 * (`/api/voice-intel/sessions`) so analytics + feedback continue to flow.
 */

type RecordingMode = 'hold' | 'toggle';

interface ConvTurn { role: 'user' | 'assistant'; text: string; ts: number; }

const Inner: React.FC<Props> = ({ storeId }) => {
  const auth = useAuth();
  const { show } = useToast();

  const [mode, setMode] = React.useState<RecordingMode>('toggle');
  const [recording, setRecording] = React.useState(false);
  const [status, setStatus] = React.useState<string>('Ready.');
  const [conversation, setConversation] = React.useState<ConvTurn[]>([]);
  const [sessionId, setSessionId] = React.useState<string | null>(null);

  const clientRef = React.useRef<VoiceGrokBrowserClient | null>(null);
  const recordingStartRef = React.useRef<number>(0);
  const utteranceCountRef = React.useRef<number>(0);
  const toolCallCountRef = React.useRef<number>(0);

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const analyserRef = React.useRef<AnalyserNode | null>(null);

  const getIdToken = React.useCallback(async () => {
    return (
      auth?.session?.getIdToken?.()?.getJwtToken?.() ??
      sessionStorage.getItem('idToken') ??
      localStorage.getItem('idToken') ??
      null
    );
  }, [auth]);

  const drawWave = React.useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth * dpr;
    const height = canvas.clientHeight * dpr;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = recording ? '#F87171' : '#4F7FFF';
    ctx.lineWidth = 2 * dpr;
    ctx.beginPath();
    const step = width / buf.length;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i] / 128.0;
      const y = (v * height) / 2;
      if (i === 0) ctx.moveTo(i * step, y);
      else ctx.lineTo(i * step, y);
    }
    ctx.stroke();
    rafRef.current = requestAnimationFrame(drawWave);
  }, [recording]);

  const stopWaveform = React.useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current = null;
  }, []);

  const handleEvent = React.useCallback((event: VoiceEvent) => {
    switch (event.type) {
      case 'connecting':
        setStatus('Connecting to BSO Voice…');
        break;
      case 'ready':
        setStatus('Listening — start speaking');
        break;
      case 'assistant_speaking_start':
        setStatus('Assistant speaking…');
        break;
      case 'assistant_speaking_end':
        setStatus('Your turn');
        break;
      case 'transcript':
        if (event.role === 'user') utteranceCountRef.current++;
        setConversation((prev) => [...prev, { role: event.role, text: event.text, ts: Date.now() }]);
        break;
      case 'tool_call':
        toolCallCountRef.current++;
        break;
      case 'error':
        show({ tone: 'error', title: 'Voice error', description: event.message });
        setStatus(`Error: ${event.code}`);
        break;
      case 'closed':
        setStatus('Session ended');
        setRecording(false);
        stopWaveform();
        break;
    }
  }, [show, stopWaveform]);

  const startRecording = async () => {
    setConversation([]);
    utteranceCountRef.current = 0;
    toolCallCountRef.current = 0;
    setStatus('Requesting mic…');

    try {
      const started = await voiceApi.startSession(getIdToken, {
        storeId,
        mode: 'cloud',
        provider: 'xai-grok',
        language: 'en-US'
      });
      setSessionId(started.sessionId);
    } catch (err: unknown) {
      // Logging is best-effort — don't block the conversation if the
      // analytics endpoint is unhappy.
      const msg = err instanceof Error ? err.message : 'session start failed';
      console.warn('voice startSession failed:', msg);
    }

    const client = new VoiceGrokBrowserClient({
      storeId,
      callSid: `web_${Date.now()}`,
      voice: 'human_Ara',
      getToken: getIdToken,
      onEvent: handleEvent
    });
    clientRef.current = client;
    await client.start();
    recordingStartRef.current = Date.now();
    setRecording(true);
    // Best-effort wave visualization off the same MediaStream Web Audio
    // already grabbed — we re-create a small analyser here against the
    // mic so the waveform reflects real input, not silence.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AC: typeof AudioContext = (window as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ac = new AC();
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      drawWave();
    } catch {
      // Waveform is decorative — silently skip if mic is taken.
    }
  };

  const stopRecording = async () => {
    const durationSeconds = Math.max(1, Math.round((Date.now() - recordingStartRef.current) / 1000));
    clientRef.current?.stop();
    clientRef.current = null;
    stopWaveform();
    setRecording(false);

    if (sessionId) {
      try {
        await voiceApi.updateSession(getIdToken, sessionId, {
          storeId,
          utteranceCount: utteranceCountRef.current,
          toolCallCount: toolCallCountRef.current,
          language: 'en-US'
        } as Record<string, unknown>);
        await voiceApi.endSession(getIdToken, sessionId, {
          storeId,
          summary: { outcome: 'completed', durationSeconds }
        } as { storeId: string; summary?: Record<string, unknown> });
      } catch {
        /* analytics best-effort */
      }
      setSessionId(null);
    }
  };

  React.useEffect(() => {
    return () => {
      clientRef.current?.stop();
      clientRef.current = null;
      stopWaveform();
    };
  }, [stopWaveform]);

  const onToggle = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  const onHoldStart = () => {
    if (mode !== 'hold') return;
    if (!recording) startRecording();
  };
  const onHoldEnd = () => {
    if (mode !== 'hold') return;
    if (recording) stopRecording();
  };

  const lastAssistantTurn = [...conversation].reverse().find((t) => t.role === 'assistant');
  const lastUserTurn = [...conversation].reverse().find((t) => t.role === 'user');

  return (
    <div className="voice-reporter" data-theme="dark">
      <header>
        <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Voice reporting</h1>
        <p style={{ color: 'var(--bso-text-secondary)', margin: 0, maxWidth: 640 }}>
          Ask a natural-language question about your restaurant&apos;s performance.
          Live duplex stream to Grok Voice via the BSO voice-relay bridge —
          managers get spoken + written summaries with real-time tool calls
          back into BSO data.
        </p>
      </header>

      <div className="voice-reporter__mic-stage">
        <SegmentedControl<RecordingMode>
          aria-label="Mic mode"
          value={mode}
          onChange={setMode}
          options={[{ value: 'toggle', label: 'Click to toggle' }, { value: 'hold', label: 'Press & hold' }]}
        />

        <button
          type="button"
          className={`voice-reporter__mic-btn${recording ? ' voice-reporter__mic-btn--recording' : ''}`}
          aria-label={recording ? 'Stop recording' : 'Start recording'}
          aria-pressed={recording}
          onClick={mode === 'toggle' ? onToggle : undefined}
          onMouseDown={onHoldStart}
          onMouseUp={onHoldEnd}
          onMouseLeave={recording && mode === 'hold' ? onHoldEnd : undefined}
          onTouchStart={onHoldStart}
          onTouchEnd={onHoldEnd}
        >
          {recording ? '■' : '🎙'}
        </button>

        <canvas ref={canvasRef} className="voice-reporter__waveform" aria-hidden="true" />

        <div className="voice-reporter__status">{status}</div>
      </div>

      {lastUserTurn && (
        <Card>
          <Card.Body>
            <div className="voice-reporter__reply-label">You asked</div>
            <div className="voice-reporter__reply-body" style={{ fontStyle: 'italic', color: 'var(--bso-text-secondary)' }}>
              &ldquo;{lastUserTurn.text}&rdquo;
            </div>
          </Card.Body>
        </Card>
      )}

      {lastAssistantTurn && (
        <div className="voice-reporter__reply">
          <div className="voice-reporter__reply-label">BSO says</div>
          <div className="voice-reporter__reply-body">{lastAssistantTurn.text}</div>
          <div style={{ marginTop: 'var(--bso-space-4)', display: 'flex', gap: 'var(--bso-space-3)' }}>
            <Button size="sm" variant="ghost" onClick={() => { setConversation([]); setStatus('Ready.'); }}>
              Ask another
            </Button>
            <Button size="sm" variant="ghost" onClick={async () => {
              try {
                await voiceApi.submitFeedback(getIdToken, { storeId, rating: 5, issue: 'accurate' });
                show({ tone: 'success', title: 'Thanks for the feedback' });
              } catch (err: unknown) {
                show({ tone: 'error', title: 'Feedback failed', description: (err as Error).message });
              }
            }}>Mark accurate</Button>
            <Button size="sm" variant="ghost" onClick={async () => {
              try {
                await voiceApi.submitFeedback(getIdToken, { storeId, rating: 2, issue: 'wrong-answer' });
                show({ tone: 'success', title: 'Flagged for review' });
              } catch (err: unknown) {
                show({ tone: 'error', title: 'Feedback failed', description: (err as Error).message });
              }
            }}>Flag as wrong</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const VoiceReportingWidget: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
